import { blicksMetaSchema, migrate, validateSchemaConsistency } from '@bliksys/schema-engine';
import type { JsonSchema, JsonValue } from '@bliksys/shared';
import { StrictMode, useCallback, useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';

import { AssetClientProvider } from './asset-origin-context.js';
import { localAssetClient } from './asset-origin.js';
import { AuthProvider, useAuth } from './auth/AuthContext.js';
import { cloudAssetClient } from './cloud-asset-client.js';
import { AppShell } from './components/AppShell.js';
import { ConfirmDialogHost } from './components/ConfirmDialog.js';
import { Faq } from './components/Faq.js';
import { InviteAccept } from './components/InviteAccept.js';
import { Landing } from './components/Landing.js';
import { LoadingScreen } from './components/LoadingScreen.js';
import { Privacy } from './components/Privacy.js';
import { ProjectSettings } from './components/ProjectSettings.js';
import { SignIn } from './components/SignIn.js';
import { ToastHost, toast } from './components/Toast.js';
import { UserMenu } from './components/UserMenu.js';
import { ProjectStoreProvider, useProjectStore } from './data/StoreContext.js';
import { demoData, demoSchema } from './demo-fixture.js';
import { downloadJson, postDeploy, postPaidDeploy, readJsonFile } from './editor-actions.js';
import { applyEnvChrome } from './environment.js';
import { useDocumentTitle } from './hooks/useDocumentTitle.js';
import { StoreProvider, useAppState, useStore } from './hooks/useStore.js';
import { confirmIfDirty, useUnsavedChangesWarning } from './hooks/useUnsavedChangesWarning.js';
import { useUrlSync } from './hooks/useUrlSync.js';
import { DEMO_PROJECT_ID } from './projects.js';
import { fetchAllRemoteRefs } from './remote-refs.js';
import {
  authErrorCode,
  type EditorMode,
  inviteToken,
  isFaqPath,
  isInvitePath,
  isPrivacyPath,
  navigateToFaq,
  navigateToLanding,
  navigateToPrivacy,
  navigateToProject,
  parseLocation,
  ROUTE_CHANGE_EVENT,
} from './router.js';
import { SchemaSaveDialog } from './schema-editor/SchemaSaveDialog.js';
import { Store } from './store.js';
import { ApiError, createApiStore } from './stores/api.js';
import { localStorageStore } from './stores/local.js';
import { detectMode } from './stores/mode.js';
import {
  FULL_ACCESS,
  type Project,
  type ProjectCapabilities,
  VersionConflictError,
} from './stores/types.js';

import './styles/tokens.css';
import './styles/base.css';

/**
 * Entry: pick free vs cloud mode by host, then render the matching
 * shell. Free mounts the localStorage store directly; cloud wraps in
 * AuthProvider and waits for the session to resolve before building
 * its API-backed store.
 */
function App(): JSX.Element {
  const mode = useMemo(() => {
    if (typeof window === 'undefined') return 'free';
    // Invite links and auth-error bounces from the API are inherently cloud
    // actions — force cloud mode so they work in dev too (the bare origin is
    // otherwise free mode).
    if (isInvitePath() || authErrorCode()) return 'cloud';
    return detectMode(window.location.host);
  }, []);
  if (mode === 'cloud') {
    return (
      <AuthProvider>
        <CloudShell />
      </AuthProvider>
    );
  }
  return (
    <ProjectStoreProvider store={localStorageStore}>
      <Root />
    </ProjectStoreProvider>
  );
}

/**
 * Cloud shell: gates everything on auth status. Builds the ApiStore
 * fresh whenever the active org changes (a switch between orgs is a
 * different store entirely; the editor remounts cleanly).
 */
function CloudShell(): JSX.Element {
  const auth = useAuth();
  const apiBaseUrl = import.meta.env.BLIKSYS_API_URL ?? 'http://localhost:3001';
  const orgSlug = auth.activeOrgSlug;
  // Track the invite route so accepting one (then navigating to the project
  // list) re-renders the shell into its normal flow.
  const [invite, setInvite] = useState(() => ({ on: isInvitePath(), token: inviteToken() }));
  useEffect(() => {
    const read = (): void => setInvite({ on: isInvitePath(), token: inviteToken() });
    window.addEventListener('popstate', read);
    window.addEventListener(ROUTE_CHANGE_EVENT, read);
    return () => {
      window.removeEventListener('popstate', read);
      window.removeEventListener(ROUTE_CHANGE_EVENT, read);
    };
  }, []);

  const store = useMemo(
    () => (orgSlug ? createApiStore({ baseUrl: apiBaseUrl, orgSlug }) : null),
    [apiBaseUrl, orgSlug],
  );

  if (auth.status === 'loading') {
    return <LoadingScreen />;
  }
  // Invite acceptance handles its own auth (it embeds sign-in when needed),
  // so it takes precedence over the unauthenticated → SignIn branch.
  if (invite.on) {
    return <InviteAccept token={invite.token} />;
  }
  if (auth.status === 'unauthenticated') {
    return <SignIn />;
  }
  if (!store || !orgSlug) {
    return <CreateOrgScreen />;
  }
  return (
    <ProjectStoreProvider store={store}>
      <Root />
    </ProjectStoreProvider>
  );
}

/**
 * Shown to an authenticated user who isn't in any org yet. The API lets
 * any signed-in user create one (becoming its owner), so the first user
 * is self-serve — no invite required. On success, AuthContext.refresh()
 * re-fetches /me and `activeOrgSlug` falls back to the new org, so the
 * shell mounts on the next render.
 */
function CreateOrgScreen(): JSX.Element {
  const auth = useAuth();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      await auth.createOrg(trimmed);
      // No state reset needed — a successful createOrg re-fetches /me and
      // this component unmounts as the shell takes over.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create organization.');
      setBusy(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: '2rem' }}>
      <form
        onSubmit={(e) => void handleSubmit(e)}
        style={{ display: 'grid', gap: '1rem', width: 'min(360px, 90vw)' }}
      >
        <div>
          <h1 style={{ margin: '0 0 0.5rem', fontSize: '1.5rem' }}>Create your organization</h1>
          <p style={{ margin: 0, opacity: 0.7 }}>
            You&apos;re not in an organization yet. Create one to get started — you&apos;ll be its
            owner.
          </p>
        </div>
        <input
          className="input"
          type="text"
          placeholder="Organization name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          required
        />
        {error && (
          <p style={{ margin: 0, color: '#c0392b' }} role="alert">
            {error}
          </p>
        )}
        <button type="submit" className="signin-submit" disabled={busy || name.trim() === ''}>
          {busy ? 'Creating…' : 'Create organization'}
        </button>
      </form>
    </div>
  );
}

/**
 * Top-level state machine driven by the URL:
 *  - path `/`         -> Landing
 *  - path `/privacy`  -> Privacy notice
 *  - path `/{id}`     -> editor for that project
 * This lets users open two projects in two tabs simultaneously.
 *
 * Project resolution is async so the same component works for the
 * localStorage store (~zero latency) and the API store (network).
 */
function Root(): JSX.Element {
  const store = useProjectStore();
  const [project, setProject] = useState<Project | null>(null);
  const [resolving, setResolving] = useState(true);
  const [mode, setMode] = useState<EditorMode>(() => parseLocation().mode);
  const [onPrivacy, setOnPrivacy] = useState<boolean>(() => isPrivacyPath());
  const [onFaq, setOnFaq] = useState<boolean>(() => isFaqPath());
  const [pathProjectId, setPathProjectId] = useState<string | null>(
    () => parseLocation().projectId,
  );

  // Re-resolve on Back/Forward and on programmatic navigation. Also runs
  // when the active store changes (e.g. an org switch in cloud mode).
  useEffect(() => {
    function readUrl(): void {
      const parsed = parseLocation();
      setOnPrivacy(isPrivacyPath());
      setOnFaq(isFaqPath());
      setMode(parsed.mode);
      setPathProjectId(parsed.projectId);
    }
    window.addEventListener('popstate', readUrl);
    window.addEventListener(ROUTE_CHANGE_EVENT, readUrl);
    return () => {
      window.removeEventListener('popstate', readUrl);
      window.removeEventListener(ROUTE_CHANGE_EVENT, readUrl);
    };
  }, []);

  // Resolve projectId → Project. Demos are local fixtures; everything
  // else goes through the active store.
  useEffect(() => {
    if (!pathProjectId) {
      setProject(null);
      setResolving(false);
      return;
    }
    if (pathProjectId === DEMO_PROJECT_ID && store.mode === 'free') {
      setProject(demoProject());
      setResolving(false);
      return;
    }
    let cancelled = false;
    setResolving(true);
    void store
      .get(pathProjectId)
      .then((p) => {
        if (cancelled) return;
        setProject(p);
        setResolving(false);
      })
      .catch(() => {
        if (cancelled) return;
        setProject(null);
        setResolving(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pathProjectId, store]);

  const handleOpenPrivacy = useCallback(() => {
    navigateToPrivacy();
    setOnPrivacy(true);
    setProject(null);
  }, []);

  const handleClosePrivacy = useCallback(() => {
    navigateToLanding();
    setOnPrivacy(false);
  }, []);

  const handleOpenFaq = useCallback(() => {
    navigateToFaq();
    setOnFaq(true);
    setProject(null);
  }, []);

  const handleCloseFaq = useCallback(() => {
    navigateToLanding();
    setOnFaq(false);
  }, []);

  const handleOpen = useCallback((p: Project) => {
    navigateToProject(p.id);
    setProject(p);
    setMode('content');
  }, []);

  const handleOpenSchema = useCallback((p: Project) => {
    // Open an existing project directly in the schema editor. Same as
    // handleOpen but starts in 'schema' mode — chosen via the row's
    // "more actions" menu when the user wants to edit structure first.
    navigateToProject(p.id, '', 'schema');
    setProject(p);
    setMode('schema');
  }, []);

  const handleOpenBlank = useCallback((p: Project) => {
    // Blank projects open straight into the schema editor — there's
    // nothing to author content against yet.
    navigateToProject(p.id, '', 'schema');
    setProject(p);
    setMode('schema');
  }, []);

  const handleOpenDemo = useCallback(() => {
    const demo = demoProject();
    navigateToProject(DEMO_PROJECT_ID);
    setProject(demo);
    setMode('content');
  }, []);

  const handleClose = useCallback(() => {
    navigateToLanding();
    setProject(null);
    setMode('content');
  }, []);

  const handleModeChange = useCallback(
    (next: EditorMode) => {
      if (!project) return;
      navigateToProject(project.id, '', next);
      setMode(next);
    },
    [project],
  );

  if (onPrivacy) {
    return <Privacy onBack={handleClosePrivacy} />;
  }
  if (onFaq) {
    return <Faq onBack={handleCloseFaq} />;
  }
  if (pathProjectId && resolving) {
    return <LoadingScreen message="Loading project…" />;
  }
  if (!project) {
    return (
      <Landing
        onOpen={handleOpen}
        onOpenSchema={handleOpenSchema}
        onOpenDemo={handleOpenDemo}
        onOpenBlank={handleOpenBlank}
        onOpenPrivacy={handleOpenPrivacy}
        onOpenFaq={handleOpenFaq}
      />
    );
  }
  return (
    <EditorRoot
      project={project}
      mode={mode}
      onClose={handleClose}
      onModeChange={handleModeChange}
    />
  );
}

function demoProject(): Project {
  return {
    id: DEMO_PROJECT_ID,
    name: 'ACME Corp. demo',
    schema: demoSchema,
    data: demoData,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

interface EditorRootProps {
  project: Project;
  mode: EditorMode;
  onClose: () => void;
  onModeChange: (mode: EditorMode) => void;
}

function EditorRoot({
  project: initialProject,
  mode,
  onClose,
  onModeChange,
}: EditorRootProps): JSX.Element {
  const store = useProjectStore();
  const [project, setProject] = useState<Project>(initialProject);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // The caller's capabilities drive which top-bar actions are shown. Free
  // mode (and the demo) is single-user, so everything is permitted.
  const [capabilities, setCapabilities] = useState<ProjectCapabilities>(FULL_ACCESS);

  useEffect(() => {
    if (store.mode !== 'cloud' || !store.getCapabilities || project.id === DEMO_PROJECT_ID) {
      setCapabilities(FULL_ACCESS);
      return;
    }
    let cancelled = false;
    void store
      .getCapabilities(project.id)
      .then((c) => {
        if (!cancelled) setCapabilities(c);
      })
      .catch(() => {
        /* leave default; the server still enforces on every write */
      });
    return () => {
      cancelled = true;
    };
  }, [store, project.id]);

  const handleSettingsOpen = (): void => setSettingsOpen(true);
  const handleSettingsClose = (): void => setSettingsOpen(false);

  useDocumentTitle(
    mode === 'schema' ? `${project.name} (schema) — Bliksys` : `${project.name} — Bliksys`,
  );

  // Two stores would compete for hash control via useUrlSync. We use one
  // at a time, keyed on mode.
  return (
    <>
      {mode === 'schema' ? (
        <SchemaEditorRoot
          project={project}
          capabilities={capabilities}
          onClose={onClose}
          onSwitchToContent={() => onModeChange('content')}
          onProjectUpdated={setProject}
          onSettings={handleSettingsOpen}
        />
      ) : (
        <ContentEditorRoot
          project={project}
          capabilities={capabilities}
          onClose={onClose}
          onSwitchToSchema={() => onModeChange('schema')}
          onSettings={handleSettingsOpen}
          onProjectUpdated={setProject}
        />
      )}
      {project.id !== DEMO_PROJECT_ID && (
        <ProjectSettings
          project={project}
          open={settingsOpen}
          onClose={handleSettingsClose}
          onSaved={(updated) => {
            setProject(updated);
            handleSettingsClose();
          }}
        />
      )}
    </>
  );
}

interface ContentEditorRootProps {
  project: Project;
  capabilities: ProjectCapabilities;
  onClose: () => void;
  onSwitchToSchema: () => void;
  onSettings: () => void;
  onProjectUpdated: (p: Project) => void;
}

function ContentEditorRoot({
  project,
  capabilities,
  onClose,
  onSwitchToSchema,
  onSettings,
  onProjectUpdated,
}: ContentEditorRootProps): JSX.Element {
  const projectStore = useProjectStore();

  // Fresh editor store per project identity. Changing name/deploy target
  // doesn't rebuild it; swapping the schema does.
  const store = useMemo(
    () => new Store({ schema: project.schema, data: project.data }),
    // project.schema triggers a fresh store if the schema was re-uploaded
    // via the settings dialog. project.data only on first mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [project.id, project.schema],
  );

  useUrlSync(store);

  // Fetch any remote $refs declared in the schema and push the result
  // into the store. The engine consults the registry to resolve
  // `$ref: 'https://...'` URLs; without this fetch step, those refs
  // throw "schema not loaded" and the editor degrades to a placeholder.
  // Aborts on store change (project switch) via the cancelled flag —
  // without this, a slow fetch from the PREVIOUS project could
  // overwrite the new project's registry.
  useEffect(() => {
    let cancelled = false;
    void fetchAllRemoteRefs(project.schema).then(({ registry, errors }) => {
      if (cancelled) return;
      if (registry.size === 0 && errors.size === 0) return;
      store.setRefRegistry(registry, errors);
    });
    return () => {
      cancelled = true;
    };
  }, [store, project.schema]);

  const handleSave = async (): Promise<void> => {
    // We persist the COMMITTED snapshot, not the working copy. An
    // in-progress draft (a half-authored item that the user hasn't
    // pressed Apply on yet) shouldn't sneak into a saved project.
    if (project.id === DEMO_PROJECT_ID) {
      store.markSaved();
      toast('Demo is read-only. Open a real project to save changes.', 'info');
      return;
    }
    try {
      const saved = await projectStore.save({ ...project, data: store.getCommitted() });
      onProjectUpdated(saved);
      store.markSaved();
      toast(projectStore.mode === 'cloud' ? 'Saved.' : 'Saved locally.', 'success');
    } catch (err) {
      if (err instanceof VersionConflictError) {
        toast('Save failed: this project was edited elsewhere. Reload to see the latest.', 'info');
        return;
      }
      if (err instanceof ApiError && err.code === 'MAINTENANCE') {
        // Expected during a release window — the server message says the
        // changes are kept locally, so this is information, not an error.
        toast(err.message, 'info');
        return;
      }
      toast(err instanceof Error ? err.message : 'Save failed.', 'error');
    }
  };

  const handleDeploy = async (): Promise<void> => {
    // Save before deploying — deploying out-of-sync state is always wrong.
    const committed = store.getCommitted();
    const schema = store.getState().schema;
    if (project.id === DEMO_PROJECT_ID) return;

    try {
      const saved = await projectStore.save({ ...project, data: committed });
      onProjectUpdated(saved);
    } catch (err) {
      if (err instanceof VersionConflictError) {
        toast('Save failed: this project was edited elsewhere. Reload to deploy.', 'info');
        return;
      }
      if (err instanceof ApiError && err.code === 'MAINTENANCE') {
        toast(err.message, 'info');
        return;
      }
      toast(err instanceof Error ? err.message : 'Save failed.', 'error');
      return;
    }

    if (projectStore.mode === 'cloud') {
      // Cloud: server resolves the deploy target from the project row
      // and decrypts secrets internally. We don't ship the config from
      // the client.
      if (!projectStore.orgSlug) {
        toast('No organization slug — cannot deploy.', 'error');
        return;
      }
      const result = await postPaidDeploy({
        orgSlug: projectStore.orgSlug,
        projectId: project.id,
      });
      if (result === false) return;
      store.markSaved();
      toast('Deployed.', 'success');
      return;
    }

    // Free mode: inline deploy target travels with the request.
    const target = project.deployTarget;
    if (!target) return;
    const result = await postDeploy({ target, schema, data: committed });
    if (result === false) return;
    store.markSaved();
  };

  const handleImport = (file: File): void => {
    readJsonFile(file, (parsed) => {
      // Content can be any JSON value — replace the whole document.
      store.replaceDocument({ data: parsed as JsonValue });
      return true;
    });
  };

  const handleExport = (): void => {
    // Pure client-side download. No deploy, no network.
    // Reads the committed snapshot — the canonical state — so an
    // in-progress draft doesn't ride along into a downloaded JSON file.
    downloadJson(store.getCommitted(), `${project.name}.json`);
  };

  // Build the AssetClient that the gallery + image input talk to. Free
  // mode signs S3 in the browser; cloud mode goes through the API's
  // asset proxy. The component tree below only sees the abstract client.
  const assetClient = useMemo(() => {
    if (projectStore.mode === 'cloud' && projectStore.orgSlug && project.assetOriginId) {
      return cloudAssetClient({
        orgSlug: projectStore.orgSlug,
        originId: project.assetOriginId,
      });
    }
    if (projectStore.mode === 'free' && project.assetOrigin) {
      return localAssetClient(project.assetOrigin);
    }
    return undefined;
  }, [projectStore, project.assetOrigin, project.assetOriginId]);

  return (
    <StoreProvider value={store}>
      <AssetClientProvider value={assetClient}>
        <UnsavedChangesGuard />
        <ContentEditorShell
          project={project}
          capabilities={capabilities}
          onSave={() => void handleSave()}
          onDeploy={() => void handleDeploy()}
          onClose={onClose}
          onSettings={onSettings}
          onSwitchToSchema={onSwitchToSchema}
          onImport={handleImport}
          onExport={handleExport}
        />
      </AssetClientProvider>
    </StoreProvider>
  );
}

interface SchemaEditorRootProps {
  project: Project;
  capabilities: ProjectCapabilities;
  onClose: () => void;
  onSwitchToContent: () => void;
  onProjectUpdated: (p: Project) => void;
  onSettings: () => void;
}

/**
 * The schema editor: an editor instance whose schema is the Bliksys
 * meta-schema and whose data is the project's content schema.
 *
 * Saving here does TWO things in one transaction:
 *   1. Migrates the project's content data against the new schema.
 *   2. Persists the new schema + migrated data via the project store.
 *
 * Showing the migration preview before save is the developer's most
 * important feedback — it tells them which content fields they're about
 * to drop/default before they commit.
 */
function SchemaEditorRoot({
  project,
  capabilities,
  onClose,
  onSwitchToContent,
  onProjectUpdated,
  onSettings,
}: SchemaEditorRootProps): JSX.Element {
  const projectStore = useProjectStore();
  // Use the project's CURRENT schema (project.schema) as the data being
  // edited, with the meta-schema as the schema describing it.
  const store = useMemo(
    () => new Store({ schema: blicksMetaSchema, data: project.schema as unknown as JsonValue }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [project.id, project.updatedAt],
  );

  useUrlSync(store);

  const [pendingSave, setPendingSave] = useState<{
    nextSchema: JsonSchema;
    migration: ReturnType<typeof migrate>;
  } | null>(null);

  const handleSaveRequested = (): void => {
    if (project.id === DEMO_PROJECT_ID) {
      toast('The demo project is read-only. Open a real project to edit its schema.', 'info');
      return;
    }
    const nextSchema = stripNullLeaves(store.getCommitted()) as JsonSchema;
    if (deepEqualJson(nextSchema, project.schema)) {
      store.markSaved();
      return;
    }
    const migration = migrate(project.schema, nextSchema, project.data);
    setPendingSave({ nextSchema, migration });
  };

  const handleSaveConfirmed = async (): Promise<void> => {
    if (!pendingSave) return;
    const { nextSchema, migration } = pendingSave;
    try {
      const updated = await projectStore.save({
        ...project,
        schema: nextSchema,
        data: migration.data,
      });
      onProjectUpdated(updated);
      store.markSaved();
      setPendingSave(null);
      const { summary } = migration;
      if (summary.dropped + summary.defaulted === 0) {
        toast('Schema saved.', 'success');
      } else {
        toast(
          `Schema saved. Migrated content: ${summary.dropped} dropped, ${summary.defaulted} defaulted.`,
          'success',
        );
      }
    } catch (err) {
      if (err instanceof VersionConflictError) {
        toast('This project was edited elsewhere. Reload to see the latest version.', 'info');
      } else {
        toast(err instanceof Error ? err.message : 'Could not save schema.', 'error');
      }
      setPendingSave(null);
    }
  };

  const handleDeploy = async (): Promise<void> => {
    if (project.id === DEMO_PROJECT_ID) return;
    const nextSchema = stripNullLeaves(store.getCommitted()) as JsonSchema;
    const migration = migrate(project.schema, nextSchema, project.data);
    let current = project;
    if (!deepEqualJson(nextSchema, project.schema)) {
      try {
        current = await projectStore.save({
          ...project,
          schema: nextSchema,
          data: migration.data,
        });
        onProjectUpdated(current);
        store.markSaved();
      } catch (err) {
        toast(err instanceof Error ? err.message : 'Could not save schema before deploy.', 'error');
        return;
      }
    }

    if (projectStore.mode === 'cloud') {
      if (!projectStore.orgSlug) {
        toast('No organization slug — cannot deploy.', 'error');
        return;
      }
      await postPaidDeploy({ orgSlug: projectStore.orgSlug, projectId: current.id });
      return;
    }

    const target = current.deployTarget;
    if (!target) return;
    await postDeploy({ target, schema: nextSchema, data: migration.data });
  };

  const handleImport = (file: File): void => {
    readJsonFile(file, (parsed) => {
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        toast('Schema must be a JSON object at its root.', 'error');
        return false;
      }
      // Replace the schema-editor's working data. Save will run migration
      // against the original project.schema as usual; the user gets the
      // standard preview + ack flow.
      store.replaceDocument({ data: parsed as JsonValue });
      return true;
    });
  };

  const handleExport = (): void => {
    // Pure client-side: download the canonical schema (the committed
    // state). A half-edited schema-draft that hasn't been applied
    // shouldn't end up in the downloaded file — same rule as the
    // content export.
    const schema = stripNullLeaves(store.getCommitted()) as JsonSchema;
    downloadJson(schema, `${project.name}.schema.json`);
  };

  return (
    <StoreProvider value={store}>
      <UnsavedChangesGuard />
      <SchemaEditorShell
        project={project}
        capabilities={capabilities}
        onSave={handleSaveRequested}
        onDeploy={() => void handleDeploy()}
        onClose={onClose}
        onSettings={onSettings}
        onSwitchToContent={onSwitchToContent}
        onImport={handleImport}
        onExport={handleExport}
        topBanner={<SchemaConsistencyBanner />}
      />
      {pendingSave && (
        <SchemaSaveDialog
          migration={pendingSave.migration}
          onConfirm={() => void handleSaveConfirmed()}
          onCancel={() => setPendingSave(null)}
        />
      )}
    </StoreProvider>
  );
}

/**
 * Recursively remove keys with `null` values from an object (and its
 * nested objects). Used by the schema editor save path: the meta-schema
 * uses `enum: [null, ...]` to let users unset optional keywords like
 * `format`, but the produced schema should omit those keys entirely
 * rather than carry literal nulls.
 *
 * Arrays and primitives pass through unchanged. We don't strip from the
 * inside of an array because `null` IS a meaningful enum value in JSON
 * Schema (e.g. `enum: ['draft', null]` to allow unset).
 */
function stripNullLeaves(value: unknown): unknown {
  if (value === null) return undefined;
  if (Array.isArray(value)) return value;
  if (typeof value !== 'object') return value;
  const result: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    const stripped = stripNullLeaves(v);
    if (stripped !== undefined) result[k] = stripped;
  }
  return result;
}

function deepEqualJson(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a === null || b === null) return false;
  if (typeof a !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((v, i) => deepEqualJson(v, b[i]));
  }
  const ao = a as Record<string, unknown>;
  const bo = b as Record<string, unknown>;
  const ak = Object.keys(ao);
  const bk = Object.keys(bo);
  if (ak.length !== bk.length) return false;
  return ak.every((k) => deepEqualJson(ao[k], bo[k]));
}

/**
 * Lives inside any StoreProvider; subscribes to the store's dirty flag
 * and installs a beforeunload guard whenever there's unsaved work.
 */
function UnsavedChangesGuard(): null {
  const state = useAppState();
  useUnsavedChangesWarning(state.dirty);
  return null;
}

/**
 * Wraps AppShell so navigation callbacks can be guarded against losing
 * unsaved changes. Lives inside StoreProvider — the parent root passes
 * raw callbacks; this shell reads the dirty flag and confirms before
 * firing them.
 */
function ContentEditorShell({
  project,
  capabilities,
  onSave,
  onDeploy,
  onClose,
  onSettings,
  onSwitchToSchema,
  onImport,
  onExport,
}: {
  project: Project;
  capabilities: ProjectCapabilities;
  onSave: () => void;
  onDeploy: () => void;
  onClose: () => void;
  onSettings: () => void;
  onSwitchToSchema: () => void;
  onImport: (file: File) => void;
  onExport: () => void;
}): JSX.Element {
  const projectStore = useProjectStore();
  const state = useAppState();
  const store = useStore();
  const hasDraft = state.draftPointer !== null;
  const guardOpts = { hasDraft, store, draftKind: state.draftKind };
  const canDeploy =
    project.id !== DEMO_PROJECT_ID &&
    capabilities.canDeploy &&
    (projectStore.mode === 'cloud'
      ? Boolean(project.deployTargetId)
      : project.deployTarget !== undefined);
  // Schema editing is developer-only; hide the affordance when the role
  // can't (the server enforces it regardless).
  const showSchemaSwitch = project.id !== DEMO_PROJECT_ID && capabilities.canEditSchema;
  return (
    <AppShell
      projectName={`Bliksys · ${project.name}`}
      onSave={onSave}
      onDeploy={canDeploy ? onDeploy : undefined}
      onClose={confirmIfDirty(state.dirty, onClose, guardOpts)}
      onSettings={project.id !== DEMO_PROJECT_ID ? onSettings : undefined}
      onSwitchToSchema={
        showSchemaSwitch ? confirmIfDirty(state.dirty, onSwitchToSchema, guardOpts) : undefined
      }
      onImport={project.id !== DEMO_PROJECT_ID ? onImport : undefined}
      onExport={onExport}
      importLabel="Import content"
      exportLabel="Export content"
      tone="content"
      topBanner={<RemoteRefErrorsBanner />}
      headerExtra={projectStore.mode === 'cloud' ? <UserMenu /> : undefined}
    />
  );
}

function SchemaEditorShell({
  project,
  capabilities,
  onSave,
  onDeploy,
  onClose,
  onSettings,
  onSwitchToContent,
  onImport,
  onExport,
  topBanner,
}: {
  project: Project;
  capabilities: ProjectCapabilities;
  onSave: () => void;
  onDeploy: () => void;
  onClose: () => void;
  onSettings: () => void;
  onSwitchToContent: () => void;
  onImport: (file: File) => void;
  onExport: () => void;
  topBanner?: React.ReactNode;
}): JSX.Element {
  const projectStore = useProjectStore();
  const state = useAppState();
  const store = useStore();
  const hasDraft = state.draftPointer !== null;
  const guardOpts = { hasDraft, store, draftKind: state.draftKind };
  const canDeploy =
    project.id !== DEMO_PROJECT_ID &&
    capabilities.canDeploy &&
    (projectStore.mode === 'cloud'
      ? Boolean(project.deployTargetId)
      : project.deployTarget !== undefined);
  return (
    <AppShell
      projectName={`Bliksys · ${project.name}`}
      onSave={onSave}
      onDeploy={canDeploy ? onDeploy : undefined}
      onClose={confirmIfDirty(state.dirty, onClose, guardOpts)}
      onSettings={project.id !== DEMO_PROJECT_ID ? onSettings : undefined}
      onSwitchToContent={confirmIfDirty(state.dirty, onSwitchToContent, guardOpts)}
      onImport={project.id !== DEMO_PROJECT_ID ? onImport : undefined}
      onExport={onExport}
      importLabel="Import schema"
      exportLabel="Export schema"
      tone="schema"
      topBanner={topBanner}
      headerExtra={projectStore.mode === 'cloud' ? <UserMenu /> : undefined}
    />
  );
}

function SchemaConsistencyBanner(): JSX.Element | null {
  const state = useAppState();
  const issues = useMemo(
    () => validateSchemaConsistency(state.data as unknown as JsonSchema),
    [state.data],
  );
  if (issues.length === 0) return null;
  return (
    <div className="schema-consistency-banner" role="status">
      <strong>
        {issues.length} consistency warning{issues.length === 1 ? '' : 's'}
      </strong>
      <ul className="schema-consistency-list">
        {issues.slice(0, 5).map((issue, i) => (
          <li key={i}>
            <code>{issue.path || '/'}</code> — {issue.message}
          </li>
        ))}
        {issues.length > 5 && <li>…and {issues.length - 5} more</li>}
      </ul>
    </div>
  );
}

/**
 * Surfaces remote `$ref` fetch failures. When the schema declares
 * `$ref: 'https://example.com/...'` and the fetch fails (network,
 * HTTP error, malformed JSON, CORS), the engine renders a "schema
 * not loaded" placeholder where the ref would be — this banner
 * tells the user WHY so they can fix the URL or arrange CORS.
 */
function RemoteRefErrorsBanner(): JSX.Element | null {
  const state = useAppState();
  if (state.refFetchErrors.size === 0) return null;
  const entries = [...state.refFetchErrors.entries()];
  return (
    <div className="schema-consistency-banner" role="status">
      <strong>
        {entries.length} remote schema{entries.length === 1 ? '' : 's'} failed to load
      </strong>
      <ul className="schema-consistency-list">
        {entries.slice(0, 5).map(([url, message]) => (
          <li key={url}>
            <code>{url}</code> — {message}
          </li>
        ))}
        {entries.length > 5 && <li>…and {entries.length - 5} more</li>}
      </ul>
    </div>
  );
}

// Mark dev tabs (favicon + title) before first paint so they're
// distinguishable from production at a glance in the browser tab strip.
applyEnvChrome();

const container = document.getElementById('root');
if (!container) throw new Error('#root not found');
createRoot(container).render(
  <StrictMode>
    <App />
    <ToastHost />
    <ConfirmDialogHost />
  </StrictMode>,
);
